import { PlusOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Image,
  Modal,
  Select,
  Upload,
  UploadFile,
  UploadProps,
} from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { ModalSizeValue } from "shared/constants";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ConfigurationItem,
  useCreateConfigurationColorMutation,
  useGetConfigurationColorQuery,
} from "shared/api";
import moment from "moment-timezone";

import { useCompanyImageUpload, useConfigurations } from "shared/hooks";
import { Colors } from "shared/themes";
import { FormInput } from "shared/ui/FormInput";
import timezones from "timezones-list";
import { selectFiltering } from "shared/helpers";
import { ColorPicker } from "./ColorPicker";

export interface ConfigurationForm {
  image: string | null;
  companyName: string | null;
  timezone: string | null;
  color: string | null;
}

interface ConfigurationsModalProps {
  isOpen: boolean;
  hideModal: () => void;
  companyNameConfiguration?: ConfigurationItem;
  timezoneConfiguration?: ConfigurationItem;
  faviconLightConfiguration?: ConfigurationItem;
  faviconDarkConfiguration?: ConfigurationItem;
}

const ConfigurationsModal: FC<ConfigurationsModalProps> = ({
  isOpen,
  hideModal,
  companyNameConfiguration,
  timezoneConfiguration,
  faviconLightConfiguration,
  faviconDarkConfiguration,
}) => {
  const { t } = useTranslation();

  const { values, handleBlur, handleChange, setFieldValue } =
    useFormikContext<ConfigurationForm>();

  const [createConfigurationColor] = useCreateConfigurationColorMutation();
  const {
    data: websiteColor,
    error,
    isError,
  } = useGetConfigurationColorQuery();

  const [documentList, setDocumentList] = useState<UploadFile<unknown>[]>([]);
  const [color, setColor] = useState("#002F5F");
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const handleDocumentList = (files: UploadFile[]) => setDocumentList(files);

  const [faviconLightFile, setFaviconLightFile] = useState<File | null>(null);
  const [faviconDarkFile, setFaviconDarkFile] = useState<File | null>(null);
  const [faviconLightPreview, setFaviconLightPreview] = useState<string | null>(
    null
  );
  const [faviconDarkPreview, setFaviconDarkPreview] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (isError && error && "status" in error && error.status === 404) {
      const defaultColor = "#002F5F";

      (async () => {
        try {
          await createConfigurationColor({
            key: "color",
            value: defaultColor,
            file: null,
          }).unwrap();

          setColor(defaultColor);
          setFieldValue("color", defaultColor);
          localStorage.setItem("color-theme", JSON.stringify(defaultColor));
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Failed to auto-create default color config:", err);
        }
      })();
    }
  }, [isError, error, createConfigurationColor, setFieldValue]);

  useEffect(() => {
    if (isOpen) {
      const stored = localStorage.getItem("color-theme");

      if (stored) {
        const parsed = JSON.parse(stored);
        setColor(parsed);
        setFieldValue("color", parsed);
      } else if (websiteColor?.value) {
        setColor(websiteColor.value);
        setFieldValue("color", websiteColor.value);
      }
    }
  }, [isOpen, websiteColor, setFieldValue]);

  const isDocumentListLength = !!documentList.length;

  const getDynamicTimezones = () => {
    return timezones.map((tz) => {
      try {
        const offset = moment.tz(tz.tzCode).format("Z");
        const cleanLabel = tz.label.replace(/\(GMT[^)]*\)\s*/g, "").trim();

        return {
          tzCode: tz.tzCode,
          label: `(UTC${offset}) (GMT${offset}) ${cleanLabel}`,
        };
      } catch {
        return { tzCode: tz.tzCode, label: tz.label };
      }
    });
  };

  const timezonesWithDST = useMemo(() => getDynamicTimezones(), []);

  const {
    uploadedFile,
    previewLogoImage,
    uploadCompanyImageProps,
    closeModalAndClearUploadedImage,
  } = useCompanyImageUpload({
    documentList,
    handleDocumentList,
    closeModal: hideModal,
  });

  const { handleConfigurationsRequests, contextHolder, handleColorChange } =
    useConfigurations({
      uploadedFile,
      closeModalAndClearUploadedImage,
      values,
      companyNameConfiguration,
      timezoneConfiguration,
      color: websiteColor?.value,
      faviconLightFile,
      faviconDarkFile,
      onAfterSave: () => {
        setFaviconLightFile(null);
        setFaviconDarkFile(null);
      },
    });

  const makeFaviconUploadProps = (
    setFile: (f: File | null) => void,
    setPreview: (url: string | null) => void
  ): UploadProps => ({
    name: "file",
    showUploadList: false,
    beforeUpload: (file) => {
      setFile(file);
      setPreview(URL.createObjectURL(file));
      return false;
    },
    accept: "image/*",
  });

  const handleTimezoneChange = (value: string) => {
    setFieldValue("timezone", value);
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={isOpen}
        onCancel={hideModal}
        onOk={handleConfigurationsRequests}
        width={ModalSizeValue.MEDIUM}
        okButtonProps={{
          style: {
            background: `${Colors.blue.primary}`,
          },
        }}
      >
        <div>
          <Title level={5}>{t("sidebar.companyName")}</Title>

          <FormInput
            value={values?.companyName}
            handleChange={handleChange}
            handleBlur={handleBlur}
            placeholder={t("sidebar.companyName")}
            fieldName="companyName"
            className="mb-4"
          />

          <Title level={5}>{t("sidebar.timezone")}</Title>

          <Select
            value={values?.timezone}
            onChange={handleTimezoneChange}
            placeholder={t("sidebar.selectTimezone")}
            className="mb-4 w-full"
            showSearch
            filterOption={(input, option) => selectFiltering(input, option)}
          >
            {timezonesWithDST.map((timezone) => (
              <Select.Option key={timezone.tzCode} value={timezone.tzCode}>
                {timezone.label}
              </Select.Option>
            ))}
          </Select>

          {values?.image ? (
            <div className="w-full flex items-center justify-between ">
              <Title level={5} className="mb-1.25">
                {t("companies.logo")}
              </Title>

              <div className="flex gap-x-3">
                {!isDocumentListLength && (
                  <Image
                    src={values?.image!}
                    className="!h-[45px] !w-[180px] !object-contain"
                  />
                )}

                <Upload {...uploadCompanyImageProps}>
                  <Button className="w-12 h-12 rounded-full p-0 !mt-1">
                    <PlusOutlined className="p-0 mb-1" />
                  </Button>
                </Upload>
              </div>
            </div>
          ) : (
            <div className="w-full flex items-center justify-between">
              <Title level={5} className="mb-1.25">
                {t("companies.logo")}
              </Title>

              <div>
                {previewLogoImage && (
                  <Avatar
                    src={previewLogoImage}
                    alt="preview"
                    className="ml-3"
                  />
                )}

                <Upload {...uploadCompanyImageProps}>
                  {!previewLogoImage && (
                    <Button className="w-8 h-8 rounded-full p-0">
                      <PlusOutlined className="p-0 mb-1" />
                    </Button>
                  )}
                </Upload>
              </div>
            </div>
          )}

          <div className="w-1/2 flex flex-col mt-3 relative">
            <div className="mb-3">
              <Title level={5} className="mb-1.25">
                {t("Color")}
              </Title>
              <Button
                onClick={async () => {
                  await handleColorChange("#002F5F");
                  window.location.reload();
                }}
              >
                Default Color
              </Button>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsColorPickerOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setIsColorPickerOpen(true);
                }
              }}
              className="flex items-center justify-between border rounded px-3 py-2 cursor-pointer hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-5 h-5 rounded-full border"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm font-mono">{color.toUpperCase()}</span>
              </div>
              <span className="text-gray-400 text-xs">(click to change)</span>
            </div>

            {isColorPickerOpen && (
              <div className="absolute right-0 top-0 mt-2 z-50">
                <ColorPicker
                  color={color}
                  onChange={(newColor) => {
                    setColor(newColor);
                    setFieldValue("color", newColor);
                  }}
                  isOpen={isColorPickerOpen}
                  onClose={() => setIsColorPickerOpen(false)}
                />
              </div>
            )}
          </div>

          {/* Favicon section */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <Title level={5} className="!mb-3">
              Default Favicon
            </Title>
            <div className="flex gap-6 items-start">
              <div className="flex gap-4">
                {/* Light Theme */}
                <div className="flex flex-col items-center gap-2">
                  <div className="rounded-lg border border-gray-200 bg-gray-100 overflow-hidden shadow-sm">
                    <div className="flex items-center gap-1 px-2 pt-2 pb-2">
                      {/* Google — left */}
                      <div
                        className="flex items-center bg-white rounded-t px-2 py-1 border border-b-white border-gray-200"
                        style={{ borderBottomColor: "white" }}
                      >
                        <img
                          src="https://www.google.com/favicon.ico"
                          alt="google"
                          className="w-5 h-5 object-contain rounded-sm"
                        />
                      </div>
                      {/* User favicon — center (active tab) */}
                      <div
                        className="flex items-center bg-white rounded-t px-2 py-1 border border-b-white border-gray-300 shadow-[0_-1px_4px_rgba(0,0,0,0.1)]"
                        style={{ borderBottomColor: "white" }}
                      >
                        {faviconLightPreview ??
                        faviconLightConfiguration?.file ? (
                          <img
                            src={
                              faviconLightPreview ??
                              faviconLightConfiguration!.file!
                            }
                            alt="favicon"
                            className="w-5 h-5 object-contain rounded-sm"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-sm bg-gray-200" />
                        )}
                      </div>
                      {/* Opera — right */}
                      <div
                        className="flex items-center bg-white rounded-t px-2 py-1 border border-b-white border-gray-200"
                        style={{ borderBottomColor: "white" }}
                      >
                        <img
                          src="https://www.google.com/s2/favicons?domain=opera.com&sz=32"
                          alt="opera"
                          className="w-5 h-5 object-contain rounded-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600">
                    Light Theme
                  </span>
                  <span className="text-xs text-gray-400">64 × 64 px</span>
                  <Upload
                    {...makeFaviconUploadProps(
                      setFaviconLightFile,
                      setFaviconLightPreview
                    )}
                  >
                    <Button size="small">Upload</Button>
                  </Upload>
                </div>

                {/* Dark Theme */}
                <div className="flex flex-col items-center gap-2">
                  <div className="rounded-lg border border-gray-700 bg-[#2d2d2d] overflow-hidden shadow-sm">
                    <div className="flex items-center gap-1 px-2 pt-2 pb-2">
                      {/* Google — left */}
                      <div className="flex items-center bg-[#1e1e1e] rounded-t px-2 py-1 border border-b-[#1e1e1e] border-gray-600">
                        <img
                          src="https://www.google.com/favicon.ico"
                          alt="google"
                          className="w-5 h-5 object-contain rounded-sm"
                        />
                      </div>
                      {/* User favicon — center (active tab) */}
                      <div className="flex items-center bg-[#1e1e1e] rounded-t px-2 py-1 border border-b-[#1e1e1e] border-gray-500 shadow-[0_-1px_4px_rgba(255,255,255,0.08)]">
                        {faviconDarkPreview ??
                        faviconDarkConfiguration?.file ? (
                          <img
                            src={
                              faviconDarkPreview ??
                              faviconDarkConfiguration!.file!
                            }
                            alt="favicon"
                            className="w-5 h-5 object-contain rounded-sm"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-sm bg-gray-600" />
                        )}
                      </div>
                      {/* Opera — right */}
                      <div className="flex items-center bg-[#1e1e1e] rounded-t px-2 py-1 border border-b-[#1e1e1e] border-gray-600">
                        <img
                          src="https://www.google.com/s2/favicons?domain=opera.com&sz=32"
                          alt="opera"
                          className="w-5 h-5 object-contain rounded-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600">
                    Dark Theme
                  </span>
                  <span className="text-xs text-gray-400">64 × 64 px</span>
                  <Upload
                    {...makeFaviconUploadProps(
                      setFaviconDarkFile,
                      setFaviconDarkPreview
                    )}
                  >
                    <Button size="small">Upload</Button>
                  </Upload>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { ConfigurationsModal };
